import { captureException } from '@mentimeter/errors/sentry';
import { MentiError } from '@mentimeter/error-utils/error';
import type {
  InteractiveCanvasComponents,
  WordCloudT,
} from '@mentimeter/question-modules-types';
import { useMemo } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import { useRagnar } from '@mentimeter/ragnar-react';
import { useUniqueSelector } from '../../shared/hooks/useUniqueSelector';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { ScreenReaderResults } from './presentation/ScreenReaderResults/ScreenReaderResults';
import { getData } from './presentation/utils/getData';
import { LegacyWordCloudVisualization } from './presentation/LegacyWordCloudVisualization';
import { WordCloudVisualization } from './presentation/WordCloudVisualization';

interface WordCloudPresentationProps {
  title: string;
  styledTitle: Record<string, unknown>;
  label: string;
  longerDescription: string;
  fontSizeOffset: -2 | -1 | 0 | 1 | 2;
  shouldHideResults: boolean;
  onRemoveWord: (key: string) => void;
  isModerationEnabled: boolean;
  results: WordCloudT['results'];
  shouldAnimate: boolean;
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
  isNewLookFlagEnabled: boolean;
}

export function WordCloudPresentation({
  title,
  styledTitle,
  label,
  longerDescription,
  fontSizeOffset,
  shouldHideResults,
  onRemoveWord,
  isModerationEnabled,
  results,
  shouldAnimate,
  CanvasVisualizationArea,
  isNewLookFlagEnabled,
}: WordCloudPresentationProps) {
  const {
    theme: {
      fonts: { body: font },
      visualizationColors: { fillColors },
    },
  } = useRagnar();

  const data = useMemo(() => {
    if (results.length === 0) return [];
    // Legacy results as string[]
    const isLegacyResults = typeof results[0] === 'string';
    if (isLegacyResults) {
      captureException(
        new MentiError('wordcloud: unsupported results format', {
          feature: 'slides',
        }),
      );
      return [];
    } else {
      return getData(results);
    }
  }, [results]);

  const isEmojiCloud = data.every(({ emojiUrl }) => Boolean(emojiUrl));

  const selector = useUniqueSelector();

  const ConditionalWrapper = CanvasVisualizationArea ?? Box;

  return (
    <Box
      width="100%"
      height="100%"
      data-testid="presentation-container"
      justifyContent="center"
    >
      <TitleSection
        title={title}
        styledTitle={styledTitle}
        description={longerDescription}
        label={label}
        fontSizeOffset={fontSizeOffset}
        showTextSizeDropdown={true}
      />

      <ConditionalWrapper flex="1" width="100%" height="75%">
        {isNewLookFlagEnabled ? (
          <WordCloudVisualization
            shouldAnimate={shouldAnimate}
            shouldHideResults={shouldHideResults}
            results={results.map((item) => ({
              text: item.text,
              value: item.count,
            }))}
          />
        ) : (
          <LegacyWordCloudVisualization
            selector={selector}
            data={data}
            emojiCloud={isEmojiCloud}
            fillColors={fillColors}
            shouldAnimate={shouldAnimate}
            font={font}
            shouldShowResults={!shouldHideResults}
            isModerationEnabled={isModerationEnabled}
            onRemoveWord={onRemoveWord}
          />
        )}
      </ConditionalWrapper>
      <ScreenReaderOnly>
        <ScreenReaderResults
          results={data}
          shouldHideResults={shouldHideResults}
        />
      </ScreenReaderOnly>
    </Box>
  );
}
